import React, { useEffect, useState, useRef } from 'react'
import classNames from 'classnames/bind'
import { useRouter } from 'next/router'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { useMutation } from 'react-query'
import { useSearchParams } from 'next/navigation'

// Components
import Image from '@/components/common/Image'
import Icon from '@/assets/icons'
import InvitationFormik from './components/Forms/InvitationFormik'
import InvitationForm from './components/Forms/InvitationForm'

import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

import { fetchUserAuthRegister } from '@/api/livemallclub/fetchUserAuthRegister'

import { VALID_PATH } from '@/constants/landingPagePath'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'
import { useAppSelector } from '@/redux/hooks'

const cx = classNames.bind(styles)

const LandingPageComponent = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const formRef = useRef<HTMLDivElement>(null)
  const fetchUserAuthRegisterMutation = useMutation(fetchUserAuthRegister)
  const [isSubmittedSuccess, setIsSubmittedSuccess] = useState<boolean>(false)
  const eventName: string | string[] | undefined = router?.query?.eventName
  const searchParams = useSearchParams()
  const refCodeByQs = searchParams?.get('ref_code')
  const refCode = !isEmpty(refCodeByQs) ? refCodeByQs : undefined
  const { isInputFocused } = useAppSelector((state) => state.common)

  const handleSubmitUserDataForm = async ({ name, email }: any) => {
    await fetchUserAuthRegisterMutation.mutateAsync({ name, email, refCode })

    setIsSubmittedSuccess(true)
  }

  useEffect(() => {
    const validEventName = Object.values(VALID_PATH)

    if (!isEmpty(eventName) && !validEventName.includes(eventName)) {
      handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.FOLLOW, isReplace: true })
    }
  }, [eventName])

  return (
    <div className={cx('landing-page', { 'landing-page-input-focused': isInputFocused })}>
      {/* Header */}
      <div className={cx('landing-page__header')}>
        <div className={cx('landing-page__header-logo')}>
          <Image src={'/images/livemallclub/landing-page-logo@3x.png'} fill alt={'LiveMallClub'} />
        </div>
        <div className={cx('landing-page__header-title')}>
          <div className={cx('landing-page__header-title-1')}>{t('Landing_Page_Invitation_Title_1')}</div>
          <div className={cx('landing-page__header-title-2')}>{t('Landing_Page_Invitation_Brand_Name')}</div>
        </div>
      </div>
      {/* Header */}
      {/* Tip */}
      <div className={cx('landing-page__tip')}>
        <span className={cx('landing-page__tip-highlight')}>{t('Landing_Page_Invitation_Brand_Name')}</span>
        {t('Landing_Page_Invitation_Tip')}
      </div>
      {/* Tip */}
      {/* Why */}
      <div className={cx('landing-page__why')}>
        <div className={cx('landing-page__why-title')}>{t('Landing_Page_Invitation_Why_Title')}</div>
        <div className={cx('landing-page__why-content')}>
          {/* Why Item */}
          <div className={cx('landing-page__why-item')}>
            <div className={cx('landing-page__why-item-left')}>
              <div className={cx('landing-page__why-item-image')}>
                <div className={cx('landing-page__why-item-image-wrapper')}>
                  <Image src={'/images/livemallclub/why-item-logo-1@3x.png'} fill alt={''} />
                </div>
              </div>
            </div>
            <div className={cx('landing-page__why-item-right')}>
              <div className={cx('landing-page__why-item-title')}>{t('Landing_Page_Invitation_Why_Item_1_Title')}</div>
              <div className={cx('landing-page__why-item-description')}>{t('Landing_Page_Invitation_Why_Item_1_Description')}</div>
            </div>
          </div>

          <div className={cx('landing-page__why-item')}>
            <div className={cx('landing-page__why-item-left')}>
              <div className={cx('landing-page__why-item-image')}>
                <div className={cx('landing-page__why-item-image-wrapper')}>
                  <Image src={'/images/livemallclub/why-item-logo-2@3x.png'} fill alt={''} />
                </div>
              </div>
            </div>
            <div className={cx('landing-page__why-item-right')}>
              <div className={cx('landing-page__why-item-title')}>{t('Landing_Page_Invitation_Why_Item_2_Title')}</div>
              <div className={cx('landing-page__why-item-description')}>{t('Landing_Page_Invitation_Why_Item_2_Description')}</div>
            </div>
          </div>

          <div className={cx('landing-page__why-item')}>
            <div className={cx('landing-page__why-item-left')}>
              <div className={cx('landing-page__why-item-image')}>
                <div className={cx('landing-page__why-item-image-wrapper')}>
                  <Image src={'/images/livemallclub/why-item-logo-3@3x.png'} fill alt={''} />
                </div>
              </div>
            </div>
            <div className={cx('landing-page__why-item-right')}>
              <div className={cx('landing-page__why-item-title')}>{t('Landing_Page_Invitation_Why_Item_3_Title')}</div>
              <div className={cx('landing-page__why-item-description')}>{t('Landing_Page_Invitation_Why_Item_3_Description')}</div>
            </div>
          </div>

          <div className={cx('landing-page__why-item')}>
            <div className={cx('landing-page__why-item-left')}>
              <div className={cx('landing-page__why-item-image')}>
                <div className={cx('landing-page__why-item-image-wrapper')}>
                  <Image src={'/images/livemallclub/why-item-logo-4@3x.png'} fill alt={''} />
                </div>
              </div>
            </div>
            <div className={cx('landing-page__why-item-right')}>
              <div className={cx('landing-page__why-item-title')}>{t('Landing_Page_Invitation_Why_Item_4_Title')}</div>
              <div className={cx('landing-page__why-item-description')}>{t('Landing_Page_Invitation_Why_Item_4_Description')}</div>
            </div>
          </div>
          {/* Why Item */}
        </div>
      </div>
      {/* Why */}
      {/* How To Join */}
      <div className={cx('landing-page__how-to-join')}>
        <div className={cx('landing-page__how-to-join-title')}>{t('Landing_Page_Invitation_How_To_Join_Title')}</div>
        <div className={cx('landing-page__how-to-join-content')}>
          <div className={cx('landing-page__how-to-join-item')}>{t('Landing_Page_Invitation_How_To_Join_Item_1')}</div>
          <div className={cx('landing-page__how-to-join-item')}>{t('Landing_Page_Invitation_How_To_Join_Item_2')}</div>
          <div className={cx('landing-page__how-to-join-item')}>{t('Landing_Page_Invitation_How_To_Join_Item_3')}</div>
        </div>
      </div>
      {/* How To Join */}

      {/* 使用者表單資料 */}
      {!isSubmittedSuccess && (
        <div ref={formRef} className={cx('landing-page__user-data-form')}>
          <div className={cx('landing-page__user-data-form-header')}>
            <div className={cx('landing-page__user-data-form-header-logo')}>
              <Icon.L_Email />
            </div>
            <div className={cx('landing-page__user-data-form-header-title')}>{t('Landing_Page_Invitation_User_Data_Form_Header_Title')}</div>
          </div>

          <InvitationFormik t={t} handleSubmitUserDataForm={handleSubmitUserDataForm}>
            <InvitationForm />
          </InvitationFormik>
        </div>
      )}
      {/* 使用者表單資料 */}

      {/* 提交成功 */}
      {isSubmittedSuccess && (
        <div className={cx('landing-page__success')}>
          <div className={cx('landing-page__success-icon')}>
            <Image src={'/images/livemallclub/submit-success@3x.png'} fill alt={''} />
          </div>
          <div className={cx('landing-page__success-title')}>{t('Landing_Page_Invitation_Success_Title')}</div>
          <div className={cx('landing-page__success-description')}>{t('Landing_Page_Invitation_Success_Description')}</div>
        </div>
      )}
      {/* 提交成功 */}
    </div>
  )
}

export default LandingPageComponent
